/**
 * Унифицированный диалог мета-правил (card_*_rules) по каталогу template_rule_types.
 */

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { cardTemplatesApi } from "@/services/card-api.service";
import type {
  CardFieldRulePutItem,
  CardFieldRuleRow,
  CardFieldRulesPutRequest,
  TemplateRuleTypeMeta,
} from "@/types/template-rule-field-editor";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Trash2 } from "lucide-react";
import { RuleValueEditor } from "@/components/admin/RuleValueEditor";

type EditableRow = {
  clientKey: string;
  rule_type_id: number;
  code: string;
  value_kind: string;
  value_schema: Record<string, unknown> | null;
  value: Record<string, unknown>;
  priority: number;
};

function rowFromApi(r: CardFieldRuleRow, idx: number): EditableRow {
  return {
    clientKey: `ex-${r.id}-${idx}`,
    rule_type_id: r.rule_type_id,
    code: r.code,
    value_kind: r.value_kind,
    value_schema: r.value_schema ?? null,
    value: { ...r.value },
    priority: r.priority,
  };
}

export type CardTemplateMetaRulesEditorProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: number;
  /** ID поля / секции / строки geometry_rules */
  resourceId: number;
  resourceLabel: string;
  appliesTo: "field" | "section" | "geometry_rule";
  queryKeySuffix: string;
  title: string;
  description: string;
  emptyHint: string;
  fetchRules: () => Promise<CardFieldRuleRow[]>;
  putRules: (body: CardFieldRulesPutRequest) => Promise<unknown>;
  canEdit: boolean;
  onSaved?: () => void;
};

export function CardTemplateMetaRulesEditor({
  open,
  onOpenChange,
  templateId,
  resourceId,
  resourceLabel,
  appliesTo,
  queryKeySuffix,
  title,
  description,
  emptyHint,
  fetchRules,
  putRules,
  canEdit,
  onSaved,
}: CardTemplateMetaRulesEditorProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<EditableRow[]>([]);

  const { data: catalog = [], isLoading: catLoading } = useQuery({
    queryKey: ["card-templates", "meta", "template-rule-types", appliesTo],
    queryFn: () => cardTemplatesApi.listTemplateRuleTypes({ applies_to: appliesTo }),
    enabled: open,
    staleTime: 300_000,
  });

  const { data: existing, isLoading: rulesLoading, refetch } = useQuery({
    queryKey: ["card-templates", templateId, queryKeySuffix, resourceId],
    queryFn: fetchRules,
    enabled: open && resourceId > 0,
  });

  useEffect(() => {
    if (!open || !existing) return;
    setRows(existing.map((r, i) => rowFromApi(r, i)));
  }, [open, existing]);

  const catalogById = useMemo(() => {
    const m = new Map<number, TemplateRuleTypeMeta>();
    catalog.forEach((c) => m.set(c.id, c));
    return m;
  }, [catalog]);

  const usedTypeIds = useMemo(() => new Set(rows.map((r) => r.rule_type_id)), [rows]);

  const saveMutation = useMutation({
    mutationFn: () =>
      putRules({
        items: rows.map(
          (r): CardFieldRulePutItem => ({
            rule_type_id: r.rule_type_id,
            value: r.value,
            priority: r.priority,
          })
        ),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["card-templates", templateId] });
      await refetch();
      onSaved?.();
      toast({
        title: "Правила сохранены",
        description: "Мета-правила и legacy-поля синхронизированы.",
      });
      onOpenChange(false);
    },
    onError: (e: Error) => {
      toast({
        title: "Ошибка сохранения",
        description: e.message || "Проверьте JSON и соответствие value_schema",
        variant: "destructive",
      });
    },
  });

  const addRule = (ruleTypeIdStr: string) => {
    const tid = Number(ruleTypeIdStr);
    if (!tid || usedTypeIds.has(tid)) return;
    const meta = catalogById.get(tid);
    if (!meta) return;
    setRows((prev) => [
      ...prev,
      {
        clientKey: `new-${tid}-${Date.now()}`,
        rule_type_id: tid,
        code: meta.code,
        value_kind: meta.value_kind,
        value_schema: meta.value_schema ?? null,
        value: defaultValueForKind(meta),
        priority: 100,
      },
    ]);
    setAddSelectKey((k) => k + 1);
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((r) => r.clientKey !== key));
  };

  const updateRow = (key: string, patch: Partial<EditableRow>) => {
    setRows((prev) => prev.map((r) => (r.clientKey === key ? { ...r, ...patch } : r)));
  };

  const [addSelectKey, setAddSelectKey] = useState(0);
  const loading = catLoading || rulesLoading;
  const availableToAdd = catalog.filter((c) => !usedTypeIds.has(c.id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        nestedAboveSheet
        className="max-h-[90vh] max-w-4xl overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="text-base">
            {title}: {resourceLabel}
          </DialogTitle>
          <p className="text-xs text-muted-foreground">{description}</p>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted-foreground py-6">Загрузка…</p>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1 flex-1 min-w-[200px]">
                <Label className="text-xs">Добавить тип правила</Label>
                <Select
                  key={addSelectKey}
                  disabled={!canEdit || availableToAdd.length === 0}
                  onValueChange={(v) => {
                    addRule(v);
                  }}
                >
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="Выберите код правила…" />
                  </SelectTrigger>
                  <SelectContent className="z-[260]">
                    {availableToAdd.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)} className="text-xs">
                        <span className="font-mono">{c.code}</span>
                        <span className="text-muted-foreground ml-2">({c.value_kind})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px] text-xs">Код</TableHead>
                  <TableHead className="text-xs min-w-[200px]">Значение (value)</TableHead>
                  <TableHead className="w-[72px] text-xs">Приоритет</TableHead>
                  <TableHead className="w-[48px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-xs text-muted-foreground">
                      {emptyHint}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.clientKey}>
                      <TableCell className="align-top text-xs font-mono">
                        {r.code}
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {r.value_kind}
                        </div>
                      </TableCell>
                      <TableCell className="align-top">
                        <RuleValueEditor
                          valueKind={r.value_kind}
                          valueSchema={r.value_schema}
                          value={r.value}
                          disabled={!canEdit}
                          onChange={(next) => updateRow(r.clientKey, { value: next })}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Input
                          type="number"
                          className="h-8 text-xs"
                          disabled={!canEdit}
                          value={r.priority}
                          onChange={(e) =>
                            updateRow(r.clientKey, {
                              priority: Number(e.target.value) || 0,
                            })
                          }
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          disabled={!canEdit}
                          onClick={() => removeRow(r.clientKey)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Закрыть
          </Button>
          {canEdit ? (
            <Button
              type="button"
              disabled={saveMutation.isPending || loading}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? "Сохранение…" : "Сохранить"}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function defaultValueForKind(meta: TemplateRuleTypeMeta): Record<string, unknown> {
  const vs = meta.value_schema;
  if (vs && typeof vs === "object" && "properties" in vs) {
    const props = (vs as { properties?: Record<string, unknown> }).properties;
    if (props && typeof props === "object") {
      const out: Record<string, unknown> = {};
      for (const [k, sub] of Object.entries(props)) {
        const t = (sub as { type?: string })?.type;
        if (t === "boolean") out[k] = false;
        else if (t === "number" || t === "integer") out[k] = 0;
        else if (t === "string") out[k] = "";
        else if (t === "array") out[k] = [];
        else if (t === "object") out[k] = {};
      }
      return out;
    }
  }
  if (meta.value_kind === "boolean") return { enabled: false };
  return {};
}
